import type { ReactNode, CSSProperties } from 'react';
import { FctIcono, FctIsotipo, FctTrama } from './marca';
import type { FctNombreIcono } from '../iconos';
import type { FctForma } from '../isotipos';

/* ---------------------------------------------------------------------------
 * FctCarcasa — piel interna: lienzo tenue, raíl verde oscuro, trama opcional.
 * ------------------------------------------------------------------------- */
export interface FctItemRail {
  clave: string;
  icono: FctNombreIcono;
  etiqueta: string;
  href?: string;
  onClick?: () => void;
}

export interface FctCarcasaProps {
  /** Forma de la herramienta. Va arriba del raíl, en color. */
  forma: FctForma;
  nombreHerramienta: string;
  items: FctItemRail[];
  activo?: string;
  /** Trama de círculos detrás del contenido. Solo en pantallas sin tabla densa. */
  trama?: boolean;
  children: ReactNode;
}

export function FctCarcasa({ forma, nombreHerramienta, items, activo, trama = false, children }: FctCarcasaProps) {
  return (
    <div className="fct-carcasa">
      {trama && <FctTrama />}
      <nav className="fct-rail" aria-label={`Navegación de ${nombreHerramienta}`}>
        <span className="fct-rail__marca">
          <FctIsotipo forma={forma} alto={26} titulo={nombreHerramienta} />
        </span>
        {items.map((it) => {
          const actual = it.clave === activo ? 'page' : undefined;
          const contenido = <FctIcono nombre={it.icono} />;
          return it.href ? (
            <a key={it.clave} className="fct-rail__item" href={it.href} aria-current={actual} title={it.etiqueta} aria-label={it.etiqueta}>
              {contenido}
            </a>
          ) : (
            <button key={it.clave} type="button" className="fct-rail__item" onClick={it.onClick} aria-current={actual} title={it.etiqueta} aria-label={it.etiqueta}>
              {contenido}
            </button>
          );
        })}
      </nav>
      <main className="fct-principal">{children}</main>
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * FctCabecera — sobre qué se trabaja, siempre arriba: camino, título, contexto, acciones.
 * ------------------------------------------------------------------------- */
export interface FctCabeceraProps {
  /** Entorno donde vive la herramienta (se dibuja en una tinta). */
  entorno?: { nombre: string; forma: FctForma };
  /** Resto del camino: herramienta, sección… */
  camino?: string[];
  titulo: string;
  contexto?: ReactNode;
  acciones?: ReactNode;
}

export function FctCabecera({ entorno, camino = [], titulo, contexto, acciones }: FctCabeceraProps) {
  const partes = [...(entorno ? [entorno.nombre] : []), ...camino];
  return (
    <header className="fct-cabecera">
      <div>
        {partes.length > 0 && (
          <p className="fct-cabecera__camino">
            {entorno && <FctIsotipo forma={entorno.forma} alto={13} tinta="una-tinta" />}
            {partes.map((p, i) => (
              <span key={i}>
                {i > 0 && <span className="fct-sep" aria-hidden="true">/ </span>}
                {p}
              </span>
            ))}
          </p>
        )}
        <h1 className="fct-titulo-pagina">{titulo}</h1>
        {contexto && <p className="fct-cabecera__contexto">{contexto}</p>}
      </div>
      {acciones && <div className="fct-cabecera__acciones">{acciones}</div>}
    </header>
  );
}

/* ---------------------------------------------------------------------------
 * FctTarjeta — superficie de trabajo. Con encabezado de gráfico: número, pregunta y nota.
 * ------------------------------------------------------------------------- */
export interface FctTarjetaProps {
  /** Número de gráfico ("01"). */
  numero?: string;
  /** Título; en gráficos, la pregunta que responde. */
  titulo?: string;
  /** Qué se mide, periodo y base. */
  nota?: ReactNode;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
}

export function FctTarjeta({ numero, titulo, nota, className, style, children }: FctTarjetaProps) {
  return (
    <section className={['fct-tarjeta', className].filter(Boolean).join(' ')} style={style}>
      {(titulo || numero) && (
        <div className="fct-tarjeta__encabezado">
          {numero && <span className="fct-tarjeta__num">{numero}</span>}
          {titulo && <h2 className="fct-titulo-tarjeta">{titulo}</h2>}
          {nota && <p className="fct-tarjeta__nota">{nota}</p>}
        </div>
      )}
      {children}
    </section>
  );
}

/* ---------------------------------------------------------------------------
 * FctKpi — etiqueta Mono, cifra Mono 28, comparación con signo, flecha y periodo.
 * La variación se colorea; la cifra no. Sin color por categoría.
 * ------------------------------------------------------------------------- */
export interface FctKpiProps {
  etiqueta: string;
  valor: string;
  unidad?: string;
  comparacion?: {
    valor: string;
    /** favorable = verde, desfavorable = rojo. Subir no siempre es bueno. */
    sentido: 'favorable' | 'desfavorable' | 'neutra';
    direccion: 'sube' | 'baja' | 'igual';
    texto: string;
  };
  /** Alternativa a la comparación: nota con nivel de atención. */
  nota?: ReactNode;
}

export function FctKpi({ etiqueta, valor, unidad, comparacion, nota }: FctKpiProps) {
  const flecha = comparacion ? { sube: '▲', baja: '▼', igual: '=' }[comparacion.direccion] : '';
  return (
    <div className="fct-tarjeta fct-kpi">
      <p className="fct-kpi__etiqueta">{etiqueta}</p>
      <p className="fct-kpi__cifra">
        {valor}
        {unidad && <small>{unidad}</small>}
      </p>
      {comparacion && (
        <p className="fct-kpi__comparacion">
          <span className={`fct-var fct-var--${comparacion.sentido}`}>
            {flecha} {comparacion.valor}
          </span>
          {comparacion.texto}
        </p>
      )}
      {!comparacion && nota && <p className="fct-kpi__comparacion">{nota}</p>}
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * FctApp — ficha de herramienta en un lanzador: isotipo en color + nombre en Roboto.
 * ------------------------------------------------------------------------- */
export interface FctAppProps {
  forma: FctForma;
  nombre: string;
  descripcion?: string;
  href?: string;
  onClick?: () => void;
}

export function FctApp({ forma, nombre, descripcion, href, onClick }: FctAppProps) {
  const cuerpo = (
    <>
      <span className="fct-app__marca">
        <FctIsotipo forma={forma} alto={forma.startsWith('2x') ? 20 : 30} />
      </span>
      <span style={{ minWidth: 0, flex: 1 }}>
        <span className="fct-app__nombre">{nombre}</span>
        {descripcion && <span className="fct-app__desc">{descripcion}</span>}
      </span>
    </>
  );
  return href ? (
    <a className="fct-app" href={href}>{cuerpo}</a>
  ) : (
    <button type="button" className="fct-app" onClick={onClick} style={{ font: 'inherit', textAlign: 'left', cursor: 'pointer' }}>
      {cuerpo}
    </button>
  );
}
